import { useEffect } from "react";
import { useLocation } from "react-router";
import { sendPageView, initGoogleTag } from "@/lib/googleTag";
import { useUserRegion } from "@/hooks/useUserRegion";
import { useLanguage } from "@/hooks/useLanguage";

const TITLE_SETTLE_MS = 3_000;

export function PageTracker() {
  const location = useLocation();
  const { regionInfo } = useUserRegion();
  const { currentLanguage } = useLanguage();

  useEffect(() => {
    if (typeof window === "undefined") return;

    // Do not track admin dashboard routes in public GA4 property
    if (location.pathname.startsWith("/admin")) return;

    initGoogleTag();

    const pagePath = location.pathname + location.search;
    const pageLocation = window.location.href;
    const previousLocation =
      sessionStorage.getItem("bloxida.previous-page-location") ||
      document.referrer;

    let lastSentTitle = "";
    const send = () => {
      if (document.title === lastSentTitle) return;
      lastSentTitle = document.title;
      sendPageView({
        title: document.title,
        path: pagePath,
        location: pageLocation,
        referrer: previousLocation || undefined,
        timeZone: regionInfo.timeZone,
        region: regionInfo.regionName,
        language: currentLanguage,
      });
      sessionStorage.setItem("bloxida.previous-page-location", pageLocation);
    };

    // Fire once on next tick — by then any synchronous SEO effect for the
    // same render has already run.
    const initialTimer = window.setTimeout(send, 0);

    // Re-send if the title node mutates within the settle window (handles
    // async-loaded article titles).
    const titleEl = document.querySelector("title");
    let observer: MutationObserver | undefined;
    if (titleEl) {
      observer = new MutationObserver(send);
      observer.observe(titleEl, {
        childList: true,
        subtree: true,
        characterData: true,
      });
    }
    const cleanupTimer = window.setTimeout(
      () => observer?.disconnect(),
      TITLE_SETTLE_MS,
    );

    return () => {
      window.clearTimeout(initialTimer);
      window.clearTimeout(cleanupTimer);
      observer?.disconnect();
    };
  }, [
    location,
    regionInfo.regionName,
    regionInfo.timeZone,
    currentLanguage,
  ]);

  return null;
}
